import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Badge,
  Divider,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import ExcalidrawViewer from "../../components/ExcalidrawViewer";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const siteMap = [
  {
    section: "Patient Journey",
    color: "blue",
    links: [
      { label: "Subscribe & Pay", path: "/subscribe/getting-started", description: "Sign up, add card, get instant access" },
      { label: "Pricing", path: "/pricing", description: "View plans and pricing" },
    ],
  },
  {
    section: "Learning (Course)",
    color: "green",
    links: [
      { label: "Course Dashboard", path: "/lm/dashboard", description: "View enrolled courses and progress" },
    ],
  },
  {
    section: "Health Assessment",
    color: "purple",
    links: [
      { label: "Take Assessment", path: "/assessment", description: "Complete your surgical readiness assessment" },
      { label: "My Assessments", path: "/assessment/history", description: "View all past assessments" },
      { label: "Compare Assessments", path: "/assessment/compare", description: "Compare first vs latest snapshot" },
    ],
  },
  {
    section: "Admin Tools",
    color: "orange",
    links: [
      { label: "All Client Assessments", path: "/assessment/admin", description: "View all client assessments with history" },
      { label: "Assessment Notifications", path: "/assessment/notifications", description: "Configure assessment event notifications" },
      { label: "Manage Domains", path: "/assessment/domains", description: "Configure assessment rating domains" },
      { label: "Custom Fields", path: "/assessment/custom-fields", description: "Add custom questions to assessments" },
      { label: "Subscription Products", path: "/bills/subscription-products", description: "Manage subscription plans and pricing" },
      { label: "Manage Subscriptions", path: "/bills/subscriptions/manage", description: "View and manage active subscriptions" },
      { label: "Billing Notifications", path: "/bills/notification-settings", description: "Configure billing event notifications" },
      { label: "All Clients", path: "/clients", description: "Manage client records and permissions" },
    ],
  },
  {
    section: "Cheatsheets",
    color: "teal",
    links: [
      { label: "Patient Journey (this page)", path: "/cheatsheets/surgready-patient-journey", description: "Visual flow diagram" },
      { label: "Assessment Module", path: "/cheatsheets/surgready-assessment-module", description: "Assessment system architecture" },
    ],
  },
];

const SurgreadyPatientJourney: React.FC = () => (
  <Box bg={getColor("background.main")} minHeight="100vh" display="flex" flexDirection="column">
    <NavbarWithCallToAction />

    <Box flex="1">
      <ExcalidrawViewer
        filePath="/excalidraw/surgready-patient-journey.excalidraw"
        title="SurgReady Patient Journey"
        subtitle="The complete patient flow from discovery to subscription, LM course, health assessment snapshots, and admin oversight"
      />

      {/* Site Map */}
      <Container maxW="1200px" py={10} px={{ base: 4, md: 6 }}>
        <Divider mb={8} />
        <VStack spacing={6} align="stretch">
          <Heading size="lg" color={getColor("text.primary")} letterSpacing="-0.02em">
            SurgReady Site Map
          </Heading>
          <Text color={getColor("text.secondary")} fontSize="sm">
            Quick links to every page in the platform
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {siteMap.map((group) => (
              <Box
                key={group.section}
                bg={getColor("background.light")}
                border="1px"
                borderColor={getColor("border.light")}
                borderRadius="xl"
                p={5}
              >
                <HStack mb={3}>
                  <Badge colorScheme={group.color} fontSize="xs" px={2} borderRadius="full">
                    {group.section}
                  </Badge>
                </HStack>
                <VStack align="stretch" spacing={2}>
                  {group.links.map((link) => (
                    <Link
                      key={link.path}
                      as={RouterLink}
                      to={link.path}
                      _hover={{ textDecoration: "none" }}
                    >
                      <Box
                        p={2}
                        borderRadius="md"
                        _hover={{ bg: getColor("background.overlay") }}
                        transition="all 0.15s"
                      >
                        <Text color={getColor("text.primary")} fontSize="sm" fontWeight="medium">
                          {link.label}
                        </Text>
                        <Text color={getColor("text.muted")} fontSize="xs">
                          {link.description}
                        </Text>
                      </Box>
                    </Link>
                  ))}
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>

    <FooterWithFourColumns />
  </Box>
);

export default SurgreadyPatientJourney;
